// Golem fork: every session entry path (panel, hash link, toolbar) lands on
// setSessionId — remembering the town HERE catches them all.
const {
  rememberTown,
  sessionIdFromPath,
  normalizeTownId
} = require("../golem/towns");
// FT-860: the night log is stashed PER TOWN (the same idiom golem/stats uses
// for the deal moment); the visibility mode is a standing setting.
const {
  loadLog,
  saveLog,
  loadMode,
  saveMode,
  // FT-874: the "require every row ticked before the night can end" setting.
  loadRequireChecks,
  saveRequireChecks,
  // FT-1168: ...and what a town that has never set either of them gets, so a
  // hop between towns RESETS rather than carrying the last town's rules over.
  DEFAULT_MODE,
  DEFAULT_CHECK_MODE
} = require("../golem/nightLog");

/**
 * FT-1168: STAND ONE TOWN'S NIGHT SETTINGS UP — the log, who is asked, and how
 * hard the checklist is enforced.
 *
 * Called from two places for one reason: boot (whatever town the URL named)
 * and every later hop between towns. It always COMMITS all three, never only
 * the ones it found — a town with nothing stored gets the defaults, which is
 * what stops the last town's rules from following you into the next one. That
 * failure mode is the whole reason these three moved off standing keys.
 */
function applyTownNight(store, townId) {
  store.commit("night/setMode", loadMode(townId) || DEFAULT_MODE);
  store.commit(
    "night/setRequireChecks",
    loadRequireChecks(townId) || DEFAULT_CHECK_MODE
  );
  store.commit(
    "night/setLog",
    loadLog(townId) || { day: 0, entries: [], staged: [] },
  );
}

// The three on/off preferences stored by PRESENCE: the key holds "1" while
// the grimoire flag is on and is gone while it is off. Boot reads presence
// only; the save reads the flag AFTER the commit, since a toggle can be
// committed bare.
const PRESENCE_FLAGS = [
  { key: "muted", mutation: "toggleMuted", flag: "isMuted" },
  { key: "static", mutation: "toggleStatic", flag: "isStatic" },
  { key: "imageOptIn", mutation: "toggleImageOptIn", flag: "isImageOptIn" },
];
// FT-1454: every commit that can move today's nomination ledger.
const NOMINATED_TODAY_TYPES = [
  "session/addHistory",
  "session/toggleNominatedToday",
  "session/remapNominatedToday",
  "session/setNominatedToday",
  "toggleNight",
  "clearEnded",
];
const flagByKey = (key) => PRESENCE_FLAGS.find((entry) => entry.key === key);
const flagByMutation = (type) =>
  PRESENCE_FLAGS.find((entry) => entry.mutation === type);

/**
 * One JSON stash, restored or skipped. A value that no longer parses, or that
 * `apply` cannot use, is left where it is and treated as absent — that part
 * of the town boots on its default instead of the whole store failing to
 * build (which blanks the app until storage is cleared by hand).
 */
function restoreStash(key, apply) {
  const raw = localStorage.getItem(key);
  if (!raw) return;
  try {
    apply(JSON.parse(raw));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[stash] "${key}" is unreadable — booting without it`, e);
  }
}

/**
 * The store plugin: puts this browser's saved town and preferences back as
 * the store is built, then keeps the stash in step with every commit.
 */
module.exports = function persistence(store) {
  // FT-1294: THE PAGE TITLE NO LONGER MOVES. `updatePagetitle` lived here and
  // flipped the tab between "Town Square" and "Grimoire" off `grimoire.isPublic`
  // — the face-down flag, now retired. It only ever ran off that flag, so with
  // the flag gone the title is whatever the document was served with, which is
  // the better answer anyway: the server writes the town's own name into the
  // page for link previews (FT-930), and this used to overwrite it on boot.
  // FT-889: the town named by the URL — a clean /<town> path, or a legacy
  // #<town> link. Empty means this url names no town at all.
  const urlTown =
    window.location.hash.substr(1) || sessionIdFromPath(window.location.pathname);

  // FT-889 / FT-1493: a bare url means you are not in a town, so no table
  // comes back with it. The seats and what rides with them (bluffs, fabled,
  // the Lunatic's bluffs, today's nominations) are dropped HERE, before the
  // restores below read them — the store never holds them, and the entry
  // screen shows. Same list leaving a town takes (townRoute's leaveTown).
  //
  // FT-889 ran this only when the stash still named a town. FT-1493 drops
  // that test: seats with NO town stash (a reload mid-preview leaves the Night
  // lab's sample table behind) stranded you on a dead table whose only way
  // out was a door. No deliberate townless table is left to protect — the
  // offline "in-person grimoire" has no way in (its Add player and game-state
  // load sit in menu tabs nothing opens), and the lab and the Role Studio
  // seed their tables after boot. The entry screen's recent-towns shelf is
  // the way back to the last town.
  //
  // The towns shelf is untouched: it is what makes you the host again when
  // you come back to /<town>.
  if (!urlTown) {
    [
      "session",
      "players",
      "bluffs",
      "lunaticBluffs",
      "fabled",
      "nominatedToday",
    ].forEach(key =>
      localStorage.removeItem(key)
    );
  }

  // ── BOOT: the restores run in a fixed order, and every one of them commits
  // through the same mutations the save half below listens to — which is
  // why the save half is only subscribed once they are all done.
  const background = localStorage.getItem("background");
  if (background) store.commit("setBackground", background);
  const restoreFlag = (key) => {
    const { mutation } = flagByKey(key);
    if (localStorage.getItem(key) !== null) store.commit(mutation, true);
  };
  restoreFlag("muted");
  restoreFlag("static");
  // Golem fork (2026-08-19): the bluffs cluster defaults SHOWN, so the stash
  // records only the HIDDEN state — an untouched browser, and every new one,
  // gets the default without a key having to exist.
  if (localStorage.getItem("bluffsHidden")) {
    store.commit("toggleBluffsOpen", false);
  }
  restoreFlag("imageOptIn");
  const zoom = localStorage.getItem("zoom");
  if (zoom) {
    const level = parseFloat(zoom);
    // an unreadable level keeps the default zoom rather than a NaN one
    if (Number.isFinite(level)) store.commit("setZoom", level);
  }
  // FT-1294: the `isGrimoire` stash is retired with the flag it held. It was
  // this browser's standing preference for which face the coins rested on;
  // there is one face now. An old key left in a browser's storage is simply
  // never read again — nothing writes it, nothing acts on it.
  // The script comes back first — its characters, then its edition — because
  // the bluffs, the Fabled and the seats all name characters by id and are
  // resolved against the maps these two build.
  restoreStash("roles", (roles) => {
    if (!Array.isArray(roles)) throw new TypeError("not a list");
    store.commit("setCustomRoles", roles);
    // a placeholder, replaced by the stored edition just below
    store.commit("setEdition", { id: "custom" });
  });
  restoreStash("edition", (edition) => {
    if (!edition || typeof edition !== "object" || Array.isArray(edition)) {
      throw new TypeError("not an edition");
    }
    store.commit("setEdition", edition);
  });
  restoreStash("bluffs", (ids) => {
    if (!Array.isArray(ids)) throw new TypeError("not a list");
    // slot 0 first: setBluff splices, so any other order shuffles them
    for (const [index, id] of ids.entries()) {
      store.commit("players/setBluff", {
        index,
        role: store.state.roles.get(id) || {},
      });
    }
  });
  // FT-1450: the Lunatic's own override, same idiom as `bluffs` above.
  restoreStash("lunaticBluffs", (ids) => {
    if (!Array.isArray(ids)) throw new TypeError("not a list");
    for (const [index, id] of ids.entries()) {
      store.commit("players/setLunaticBluff", {
        index,
        role: store.state.roles.get(id) || {},
      });
    }
  });
  restoreStash("fabled", (list) => {
    if (!Array.isArray(list)) throw new TypeError("not a list");
    const known = store.state.fabled;
    store.commit("players/setFabled", {
      fabled: list.map((entry) => known.get(entry.id) || entry),
    });
  });
  restoreStash("players", (seats) => {
    if (!Array.isArray(seats)) throw new TypeError("not a list");
    if (!seats.length) return;
    // FT-861: the believed role is stashed as an id beside the true one and is
    // resolved the same way — null (not {}) when the seat believes the truth,
    // because "no belief" is what golem/belief.js tests for.
    const roleById = id =>
      store.state.roles.get(id) || store.getters.rolesJSONbyId.get(id);
    store.commit(
      "players/set",
      seats.map((player) => ({
        ...player,
        role: roleById(player.role) || {},
        believedRole: player.believedRole
          ? roleById(player.believedRole) || null
          : null
      })),
    );
  });
  const playerId = localStorage.getItem("playerId");
  if (playerId) store.commit("session/setPlayerId", playerId);
  // FT-889: the URL is the one thing that says which town you are in, so this
  // block no longer RESTORES a session. It used to put you back into your last
  // town whenever the URL named none — which made the entry screen unreachable
  // except by the leave pill. A bare URL is now the entry screen, always.
  //
  // Kept as a corroborating READ of the ROLE: when the URL already names the
  // same town, the stash's spectator flag is seeded here so nothing flickers
  // before socket.js's boot resolver runs. That resolver (golem/townRoute →
  // resolveTownRole, reading the towns shelf) commits after this plugin and
  // wins any disagreement — it is the authority on who this browser is.
  if (localStorage.getItem("session")) {
    try {
      const [spectator, sessionId] = JSON.parse(localStorage.getItem("session"));
      if (urlTown && normalizeTownId(urlTown) === normalizeTownId(sessionId)) {
        store.commit("session/setSpectator", spectator);
      }
    } catch (e) {
      // an unreadable stash says nothing; the shelf answers on its own
    }
  }
  // FT-931: THE ENDED TOWN, restored before the socket ever opens.
  //
  // Every other synced-but-unpersisted field (isNight, nomination) is safe to
  // lose on reload because SOMEONE ELSE — the host — still holds the truth and
  // corrects it the moment a resync lands. isEnded has no such backstop: the
  // HOST is the authority on it, and a host who reloads their own tab has
  // nobody else to be corrected BY. Without this, the host's own refresh
  // after ending a game would silently un-end it — broadcasting isEnded:false
  // to every player who reconnects afterward, exactly backwards.
  //
  // Same idiom `players`/`session` already use: a flat, single-slot stash (no
  // per-town key), because this browser only ever mirrors one town at a time.
  if (localStorage.getItem("gameEnded")) {
    try {
      const { winningTeam } = JSON.parse(localStorage.getItem("gameEnded"));
      store.commit("endGame", winningTeam);
    } catch (e) {
      // an unreadable stash says nothing; the game resumes live
    }
  }
  // FT-1454: today's nomination badges — the HOST's copy, for isEnded's
  // reason above (a host who reloads has nobody to be corrected by). Tied to
  // its town: only the town the URL names gets it back. A player never
  // writes this stash; the host's sync is a player's truth.
  restoreStash("nominatedToday", (stash) => {
    if (!stash || typeof stash !== "object")
      throw new TypeError("not a ledger");
    if (
      urlTown &&
      normalizeTownId(urlTown) === normalizeTownId(stash.town || "")
    ) {
      store.commit("session/setNominatedToday", stash);
    }
  });
  /**** FT-860: night sheet + log ****/
  // FT-1168: all THREE of these belong to a town now — the log, who is asked
  // (mode) and how hard the list is enforced. Read whichever town the block
  // above restored; setSessionId's own handler below catches every later hop.
  // An in-person grimoire has no town and reads the "" slot, which is a real
  // key for the two settings (see nightLog's own note) and none for the log.
  applyTownNight(store, store.state.session.sessionId);

  // ── SAVE: subscribed only now, after every restore above has run (a live
  // subscriber would rewrite keys that were still waiting to be read).
  store.subscribe(({ type, payload }, state) => {
    // FT-1454: the host's nomination ledger, after every commit that can
    // move it (the night and a new game clear it inside their own
    // mutations). Players never write it — see the restore above.
    if (NOMINATED_TODAY_TYPES.includes(type) && !state.session.isSpectator) {
      localStorage.setItem(
        "nominatedToday",
        JSON.stringify({
          town: state.session.sessionId,
          ...state.session.nominatedToday,
        }),
      );
    }
    const presence = flagByMutation(type);
    if (presence) {
      if (state.grimoire[presence.flag]) {
        localStorage.setItem(presence.key, 1);
      } else {
        localStorage.removeItem(presence.key);
      }
    }
    switch (type) {
      // FT-931 / FT-1294: the ended town is stashed so a reload lands back in
      // it. The tab-title half of this case went with the face-down flag —
      // see the note at the top of this plugin.
      case "endGame":
      case "clearEnded":
        if (state.session.isEnded) {
          localStorage.setItem(
            "gameEnded",
            JSON.stringify({ winningTeam: state.session.winningTeam })
          );
        } else {
          localStorage.removeItem("gameEnded");
        }
        break;
      case "setBackground":
        if (!payload) {
          localStorage.removeItem("background");
        } else {
          localStorage.setItem("background", payload);
        }
        break;
      case "toggleBluffsOpen":
        if (state.grimoire.isBluffsOpen) {
          localStorage.removeItem("bluffsHidden");
        } else {
          localStorage.setItem("bluffsHidden", 1);
        }
        break;
      case "setZoom":
        // zero is the default zoom, so it is stored as no key at all
        if (payload === 0) {
          localStorage.removeItem("zoom");
        } else {
          localStorage.setItem("zoom", payload);
        }
        break;
      case "setEdition":
        // the payload as committed, not the copy the mutation kept in state
        localStorage.setItem("edition", JSON.stringify(payload));
        // a bundled edition brings its own characters; drop the custom ones
        if (state.edition.isOfficial) localStorage.removeItem("roles");
        break;
      case "setCustomRoles":
        if (payload.length) {
          localStorage.setItem("roles", JSON.stringify(payload));
        } else {
          localStorage.removeItem("roles");
        }
        break;
      case "players/setBluff":
        // one id per slot; an empty slot has none and is stored as null
        localStorage.setItem(
          "bluffs",
          JSON.stringify(
            state.players.bluffs.map((slot) => (slot ? slot.id : undefined)),
          ),
        );
        break;
      // FT-1450: the Lunatic's own override, same idiom as its own mutation.
      case "players/setLunaticBluff":
        localStorage.setItem(
          "lunaticBluffs",
          JSON.stringify(
            state.players.lunaticBluffs.map((slot) =>
              slot ? slot.id : undefined,
            ),
          ),
        );
        break;
      case "players/setFabled":
        // a bundled Fabled is stored by id; a custom one has nowhere to be
        // looked up from, so it is stored whole
        localStorage.setItem(
          "fabled",
          JSON.stringify(
            state.players.fabled.map((entry) =>
              entry.isCustom ? entry : { id: entry.id },
            ),
          ),
        );
        break;
      // FT-1163: `players/setDeathMoment` joins this list. The death moment is
      // stamped by its own mutation so it never reaches the wire (see that
      // mutation), and a type missing from here would mean a host reload kept
      // THAT a seat died and dropped WHEN. The stash keeps whole player
      // objects, so the two fields ride along with no projection change.
      case "players/add":
      case "players/update":
      case "players/setDeathMoment":
      case "players/remove":
      case "players/clear":
      case "players/set":
      case "players/swap":
      case "players/move":
        // every seat whole, in seat order, its characters by id — the seat
        // spread FIRST so role/believedRole keep their place in its key order
        if (state.players.players.length) {
          localStorage.setItem(
            "players",
            JSON.stringify(
              state.players.players.map((player) => ({
                ...player,
                role: player.role.id || {},
                // FT-861: what this seat's player was TOLD they are, by id;
                // null when they were told the truth
                believedRole:
                  (player.believedRole && player.believedRole.id) || null
              })),
            ),
          );
        } else {
          localStorage.removeItem("players");
        }
        break;
      case "session/setSessionId":
        if (payload) {
          // [spectator?, the town id exactly as committed]
          localStorage.setItem(
            "session",
            JSON.stringify([state.session.isSpectator, payload]),
          );
          // Remember the SANITIZED id (state, not payload) — the shelf must
          // name the town the socket actually joins.
          rememberTown(
            state.session.sessionId,
            state.session.isSpectator ? "player" : "host"
          );
          // FT-860: a town carries its own night log — hopping to another
          // town must never show the last one's. An unknown town starts clean.
          // FT-1168: and its own checklist settings, for the same reason and
          // by the same rule — the Game settings tab is about THIS town.
          applyTownNight(store, state.session.sessionId);
        } else {
          localStorage.removeItem("session");
        }
        break;
      // FT-860 — the night sheet. The mode is a standing setting; the log and
      // the day counter are stashed against the town they belong to. The day
      // moves inside toggleNight (see the root mutation), so the phase flip
      // has to write too or a reload would lose which night it is.
      // FT-882: removeEntry joins them — a deleted row has to leave the stash
      // as well, or a reload brings it straight back.
      // FT-1168: THE HOST WRITES, NOBODY ELSE.
      //
      // Both of these also arrive on a PLAYER's client over the wire (see
      // socket.js's own `night/setMode` case) — that is the host telling the
      // table what the night is, not this browser choosing anything. Writing
      // it would mean the same person, hosting that town themselves later,
      // silently inheriting the previous storyteller's rules as if they had
      // set them, which is exactly the confusion the per-town-per-host key
      // exists to prevent. The spectator flag is the discriminator.
      case "night/setMode":
        if (!state.session.isSpectator)
          saveMode(state.session.sessionId, state.night.mode);
        break;
      case "night/setRequireChecks":
        if (!state.session.isSpectator)
          saveRequireChecks(state.session.sessionId, state.night.requireChecks);
        break;
      // FT-1173: the staged-deaths queue rides the same stash (see night.js's
      // staged note) — staging, un-staging and the End-night commit's clear
      // all rewrite it, and every existing writer now carries it along.
      case "toggleNight":
      case "night/setDay":
      case "night/setLog":
      case "night/addEntry":
      case "night/patchEntry":
      case "night/removeEntry":
      case "night/stageDeath":
      case "night/unstageDeath":
      case "night/clearStaged":
        saveLog(
          state.session.sessionId,
          state.night.day,
          state.night.entries,
          state.night.staged,
        );
        break;
      case "session/setPlayerId":
        if (payload) {
          localStorage.setItem("playerId", payload);
        } else {
          localStorage.removeItem("playerId");
        }
        break;
    }
  });
};
